import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import EditorBytesStrip from "@/components/EditorBytesStrip";
import { Quote, Feather, Calendar } from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("hi-IN", { year: "numeric", month: "long", day: "numeric" });
}

export default function EditorPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/news", { params: { editorial: true, limit: 60 } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <>
      <PublicHeader />

      {/* Hero banner — Editor's Column */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1E3A8A] text-white overflow-hidden" data-testid="editor-hero">
        <div className="absolute -top-16 -right-10 opacity-[0.06] text-[380px] font-devnagri-heading font-black leading-none select-none pointer-events-none">"</div>
        <div className="max-w-7xl mx-auto px-4 py-14 md:py-20 relative">
          <div className="flex items-center gap-3 mb-4">
            <Feather className="w-5 h-5 text-amber-300"/>
            <span className="uppercase tracking-[0.3em] text-[11px] text-amber-300 font-black">Editor's Column · संपादकीय स्तंभ</span>
          </div>
          <h1 className="font-devnagri-heading text-4xl sm:text-5xl md:text-6xl font-black leading-tight max-w-4xl">
            प्रधान संपादक की <span className="text-amber-300">कलम से</span>
          </h1>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-end gap-5 sm:gap-6">
            {/* Chief Editor Portrait */}
            <div className="shrink-0" data-testid="editor-portrait">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-amber-400 via-amber-300 to-amber-500 rounded-full blur-sm opacity-70"></div>
                <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-[3px] border-amber-300 shadow-2xl ring-2 ring-amber-400/30 ring-offset-2 ring-offset-[#0F172A]">
                  <img
                    src="/jakir-momin-boss.png"
                    alt="JAKIR MOMIN — Chief Editor"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-amber-400 text-[#0F172A] text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shadow-lg border border-white/40">
                  Chief
                </div>
              </div>
            </div>

            <div>
              <div className="text-amber-200/80 text-xs uppercase tracking-widest font-admin">Chief Editor</div>
              <div className="font-devnagri-heading text-3xl md:text-4xl font-black text-amber-300 tracking-wider" style={{ fontFamily: "'Times New Roman', serif", fontStyle: "italic" }}>
                JAKIR MOMIN
              </div>
              <div className="text-slate-300 text-sm mt-1">— INDIAN-NEWS-21 · भारत की आवाज़</div>
            </div>
            <div className="hidden sm:block h-16 w-px bg-white/20"></div>
            <p className="text-slate-300 max-w-xl leading-relaxed text-sm md:text-base">
              हर विचारशील समाचार के पीछे एक दृष्टिकोण होता है। यहाँ पढ़िए प्रधान संपादक <b className="text-amber-300">JAKIR MOMIN</b> के चुनिंदा संपादकीय — बेबाक, ज़मीनी और भरोसेमंद।
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 py-12" data-testid="editor-page">
        <EditorBytesStrip />
        {loading ? (
          <div className="text-slate-500 py-20 text-center">लोड हो रहा है…</div>
        ) : items.length === 0 ? (
          <div className="text-slate-500 py-20 text-center">फ़िलहाल कोई संपादकीय उपलब्ध नहीं है।</div>
        ) : (
          <>
            {/* Featured editorial */}
            {featured && (
              <Link to={`/article/${featured.slug}`} className="block group mb-14 border-l-4 border-[#EA580C] pl-6 md:pl-10" data-testid={`editor-featured-${featured.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                  <div className="md:col-span-3">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="tag-cat">{featured.category}</span>
                      <span className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3"/> {relTime(featured.created_at)}</span>
                    </div>
                    <Quote className="w-8 h-8 text-[#EA580C] mb-3"/>
                    <h2 className="font-devnagri-heading text-3xl md:text-5xl font-black text-[#0F172A] leading-tight news-title group-hover:text-[#DC2626] transition">
                      {featured.title}
                    </h2>
                    <p className="mt-5 text-slate-600 leading-relaxed text-lg line-clamp-4">{featured.excerpt}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-admin font-bold text-[#EA580C] group-hover:gap-3 transition-all">
                      पूरा संपादकीय पढ़ें <span>→</span>
                    </div>
                  </div>
                  {featured.image_url && (
                    <div className="md:col-span-2 img-hover-zoom rounded-lg aspect-[4/3] overflow-hidden bg-slate-100">
                      <img src={featured.image_url} alt={featured.title} className="w-full h-full object-cover"/>
                    </div>
                  )}
                </div>
              </Link>
            )}

            {/* Grid of rest */}
            {rest.length > 0 && (
              <>
                <div className="flex items-center gap-3 mb-8">
                  <div className="h-px bg-slate-200 flex-1"></div>
                  <h3 className="font-devnagri-heading text-2xl font-black text-[#0F172A]">और संपादकीय</h3>
                  <div className="h-px bg-slate-200 flex-1"></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {rest.map((e) => (
                    <Link key={e.id} to={`/article/${e.slug}`} className="group block bg-white rounded-lg border border-slate-100 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition" data-testid={`editor-item-${e.id}`}>
                      {e.image_url && (
                        <div className="img-hover-zoom aspect-video bg-slate-100">
                          <img src={e.image_url} alt={e.title} className="w-full h-full object-cover"/>
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest mb-2">
                          <span className="text-[#EA580C] font-bold">{e.category}</span>
                          <span className="text-slate-400">{relTime(e.created_at)}</span>
                        </div>
                        <h4 className="font-devnagri-heading font-black text-lg leading-tight text-[#0F172A] group-hover:text-[#DC2626] transition line-clamp-3">
                          {e.title}
                        </h4>
                        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{e.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>

      <PublicFooter />
    </>
  );
}
